import { Box, Button, makeStyles, useTheme, useMediaQuery, IconButton, Theme } from "@material-ui/core";
import { QuoteLineItem } from "../../../api/Quote/QuoteLineItem";
import { Quote } from "../../../api/Quote/Quote";
import Slider from "react-slick";
import NavigateBeforeOutlinedIcon from "@material-ui/icons/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@material-ui/icons/NavigateNextOutlined";
import { hasAddons, hasSubscriptions, paymentTypeTitle, quateAddonQuantityValue } from "../../../app/utils";
import QuoteButtonAdd from "./QuoteButtonAdd";
import { getFirstImage, ProductSubscription } from "../../../api/Product/ProductSubscriptionApi";
import { history } from "../../../app/rootReducer";
import QuoteButtonAddSubscription from "./QuoteButtonAddSubscription";
import { TF } from "../../../components/typography/TF";
import QuoteAddonChangeQuantity from "./QuoteAddonChangeQuantity";

const QuoteAddons = ({
  quote,
  addons,
  subscriptions,
  onChangeQuantity,
  onAddAddon,
  onInfoDialogOpen,
  onAddSubscription
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  if (!hasAddons(addons) && !hasSubscriptions(subscriptions)) {
    return null;
  }

  const addonView = function (addon: QuoteLineItem) {
    if (!addon) {
      return null;
    }

    const onView = function () {
      onInfoDialogOpen(addon);
    };

    const hideOnMin = addon.isFixedQuantity || (addon.quantity === 1 && addon.maxQuantity === 1);

    return (
      <Box className={classes.box} p={isSmall ? 0.2 : 3} key={addon.name}>
        <Box boxShadow={3} p={isSmall ? 1.5 : 3}>
          <Box className={classes.imageBox}>
            <img className={classes.image} alt={addon.name} src={addon.imageUrl} />
          </Box>
          <Box textAlign="center">
            <Box className={classes.title}>
              <TF variant="h6" bold>
                {addon.name}
              </TF>
            </Box>
            <Box className={classes.description}>
              <TF>{addon.highLevelDescription}</TF>
            </Box>
            <Box
              display="flex"
              height="50px"
              justifyContent={hideOnMin ? "center" : "space-between"}
              alignItems="center"
              mt={1}
            >
              <TF variant="h6" bold format="price">
                {quateAddonQuantityValue(addon)}
              </TF>
              {!hideOnMin && (
                <QuoteAddonChangeQuantity
                  addon={addon}
                  onChangeQuantity={onChangeQuantity}
                  hideOnMin={true}
                  hideOnSmall={false}
                  showRecommend={false}
                  showOneLine={true}
                  disabled={false}
                />
              )}
            </Box>
            <Box display="flex" justifyContent="space-between" mt={isSmall ? 1 : 4}>
              <Button size="small" onClick={onView}>
                <TF color="secondary">View Details</TF>
              </Button>
              <QuoteButtonAdd addon={addon} onAddAddon={onAddAddon} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const subscriptionView = (subscription: ProductSubscription) => {
    if (!subscription) {
      return null;
    }

    const onView = function () {
      history.push("/protect");
    };

    return (
      <Box className={classes.box} p={isSmall ? 0.2 : 3} key={subscription.name}>
        <Box boxShadow={3} p={isSmall ? 1.5 : 3}>
          <Box className={classes.imageBox}>
            <img className={classes.image} alt={subscription.name} src={getFirstImage(subscription)?.url} />
          </Box>
          <Box textAlign="center">
            <Box className={classes.title}>
              <TF variant="h6" bold>
                {subscription.name}
              </TF>
            </Box>
            <Box className={classes.description}>
              <TF>{subscription.highLevelDescription}</TF>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" height="50px" mt={1}>
              <TF component="span">from&nbsp;</TF>
              <TF variant="h6" bold format="price" component="span">
                {subscription.prices[0].price}
              </TF>
              <TF variant="h6" component="span" bold>
                &nbsp;{paymentTypeTitle(subscription.prices[0].paymentType, true)}
              </TF>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={isSmall ? 1 : 4}>
              <Button size="small" onClick={onView}>
                <TF color="secondary">View Details</TF>
              </Button>

              <QuoteButtonAddSubscription
                quote={quote}
                subscription={subscription}
                onAddSubscription={onAddSubscription}
                fullWidth={isSmall}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const totalLength = (subscriptions?.length ?? 0) + (addons?.length ?? 0);

  const length = totalLength > 2 ? 3 : totalLength;

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    swipeToSlide: true,
    slidesToShow: isSmall ? 1 : length,
    slidesToScroll: isSmall ? 1 : length,
    nextArrow: <NextArrow isSmall={isSmall} />,
    prevArrow: <PrevArrow isSmall={isSmall} />
  };

  return (
    <Box p={isSmall ? 0 : 3} mt={isSmall ? 1 : 0} textAlign="center">
      <Slider {...settings} autoplay={true} autoplaySpeed={8000}>
        {hasAddons(addons) && addons!.map((li) => addonView(li))}
        {hasSubscriptions(subscriptions) && subscriptions!.map((li) => subscriptionView(li))}
      </Slider>
    </Box>
  );
};

export default QuoteAddons;

interface Props {
  quote: Quote;
  addons?: QuoteLineItem[];
  subscriptions: ProductSubscription[] | null;
  onAddAddon: (addon: QuoteLineItem) => void;
  onInfoDialogOpen: (addon: QuoteLineItem) => void;
  onAddSubscription: (subscription: ProductSubscription) => void;
  onChangeQuantity: (addon: QuoteLineItem, quantity: number) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  box: {
    maxWidth: "400px",
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      maxWidth: "335px"
    }
  },
  imageBox: {
    height: "300px",
    [theme.breakpoints.down("md")]: {
      height: "265px"
    }
  },
  image: {
    margin: "0 auto",
    display: "block",
    maxHeight: "300px",
    [theme.breakpoints.down("md")]: {
      maxHeight: "265px"
    }
  },
  icon: {
    verticalAlign: "text-top"
  },
  title: {
    height: "55px",
    marginTop: theme.spacing(1)
  },
  description: {
    marginTop: theme.spacing(1),
    display: "grid",
    textAlign: "center",
    alignItems: "center",
    height: "65px"
  }
}));

const NextArrow = ({ className, style, onClick, isSmall }: any) => {
  const color = onClick == null ? "disabled" : "secondary";
  const mobile = isSmall
    ? {
        right: "0px",
        marginTop: "-125px"
      }
    : {
        right: "0px",
        marginTop: "-65px"
      };
  return (
    <Box className={className} style={{ ...mobile }}>
      <IconButton onClick={onClick}>
        <NavigateNextOutlinedIcon color={color} fontSize="large" style={{ ...style, display: "block" }} />
      </IconButton>
    </Box>
  );
};

const PrevArrow = ({ className, style, onClick, isSmall }: any) => {
  const color = onClick == null ? "disabled" : "secondary";
  const mobile = isSmall
    ? {
        left: "-40px",
        marginTop: "-125px"
      }
    : {
        left: "-40px",
        marginTop: "-65px"
      };
  return (
    <Box className={className} style={{ ...mobile }}>
      <IconButton onClick={onClick}>
        <NavigateBeforeOutlinedIcon color={color} fontSize="large" style={{ ...style, display: "block" }} />
      </IconButton>
    </Box>
  );
};
